import * as stylex from '@stylexjs/stylex';
import { Slider } from '@base-ui/react/slider';
import { useLayoutEffect, useRef, useState } from 'react';
import { motion, MotionConfig } from 'motion/react';
import '../../../../../styles/tokens.stylex';
import { spacing, typographyColor } from '../../../../../styles/tokens.stylex';
import { formStyles } from '@/components/Form/form.styles';
import { typography } from '../../../../../styles/typography';
import '../../../form.tokens.stylex';
import { formTokens } from '../../../form.tokens.stylex';
import { FieldControlContext } from '../Field';
import { use } from 'react';
import { a11y } from '../../../../../styles/a11y';
import { tinaField } from 'tinacms/react';
import { ConversionFormStepsFieldsSliderField } from '@/tina/__generated__/types';
type SliderInputProps = {
  data: ConversionFormStepsFieldsSliderField;
};
export function SliderInput({
  data,
  ...props
}: SliderInputProps) {
  const fieldControl = use(FieldControlContext);
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRowRef = useRef<HTMLDivElement>(null);
  const [labelRowWiderThanRoot, setLabelRowWiderThanRoot] = useState(false);
  const values = data.values ?? [];
  useLayoutEffect(() => {
    const root = rootRef.current;
    const measureRow = measureRowRef.current;
    if (!root || !measureRow) return;
    const update = () => {
      const rootWidth = root.getBoundingClientRect().width;
      const labelsWidth = measureRow.getBoundingClientRect().width;
      setLabelRowWiderThanRoot(labelsWidth > rootWidth);
    };
    update();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(update);
    });
    ro.observe(root);
    ro.observe(measureRow);
    return () => ro.disconnect();
  }, []);
  function handleValueChange(value: number | readonly number[]) {
    fieldControl.onValueChange(data.values?.[value as number]?.value ?? '');
  }
  const value = fieldControl.value ? data.values?.findIndex(v => v?.value === fieldControl.value) ?? 0 : 0;
  return <MotionConfig transition={{
    type: 'spring',
    stiffness: 1000,
    damping: 65,
    mass: 2
  }}>
      <Slider.Root {...fieldControl} ref={e => {
      rootRef.current = e;
      fieldControl.ref(e);
    }} defaultValue={data.defaultValue ? data.values?.findIndex(v => v?.value === data.defaultValue) ?? 0 : 0} value={value} onValueChange={handleValueChange} min={0} max={values.length - 1} step={1} data-label-row-overflow={labelRowWiderThanRoot ? 'true' : undefined} className="x16pmaob x1phq5z5 x61l1ut x1l3qa4k x12h1iku x78zum5 xdt5ytf x1n2onr6" {...props}>
        <Slider.Label {...stylex.props(formStyles.label)} data-tina-field={tinaField(data, 'label')}>
          {data.label}
        </Slider.Label>
        <Slider.Control className="x6s0dn4 x78zum5 x1n2onr6 x1qx5ct2">
          <Slider.Track className="xgwa0xu x1n2onr6 xols6we xh8yej3">
            <Slider.Indicator className="x1fuwk4f x10l6tqk x5yr21d" render={<motion.div layout />} />

            {/* Render Tick Marks */}
            {values.map((v, i) => <div key={i} {...stylex.props(sx.tick(i / (values.length - 1) * 100), i <= value && sx.tickActive)} />)}

            <Slider.Thumb {...stylex.props(a11y.focusRing, sx.thumb)} render={<motion.div layout />} />
          </Slider.Track>
        </Slider.Control>
        {/* Intrinsic width of all labels (same typography as the visible row). */}
        <div ref={measureRowRef} aria-hidden className="x3ybb33 x78zum5 x1nhvcw1 x47corl x10l6tqk xlshs6z xuxw1ft x3rpodo x1x1rfll x13vifvy xezivpi">
          {values.map((v, i) => <span key={i} {...stylex.props(typography.small, sx.tickLabel)}>
              {v?.value}
            </span>)}
        </div>
        <div className="x1gkhxyl x12h1iku x1n2onr6 x193iq5w">
          {labelRowWiderThanRoot ? <div className="x3ybb33 x78zum5 xyi9k72 xl56j7k x47corl">
              <span {...stylex.props(typography.small, sx.tickLabel, sx.labelActive)}>
                {values[value]?.value}
              </span>
            </div> : <div className="x3ybb33 x78zum5 x1qughib xyi9k72">
              {values.map((v, i) => <span key={i} {...stylex.props(typography.small, sx.tickLabel, i === value && sx.labelActive)} onClick={() => fieldControl.onValueChange(v?.value ?? '')}>
                  {v?.value}
                </span>)}
            </div>}
        </div>
      </Slider.Root>
    </MotionConfig>;
}
const _temp = {
  kWkggS: "x1enj7ex",
  kkrTdU: "x1ypdohk",
  kVAEAm: "x10l6tqk",
  k3aq6I: "x11lhmoz",
  kY2c9j: "x1vjfegm",
  kZKoxP: "x170jfvy",
  kbCHJM: "xgq9j65",
  k87sOh: "xwa60dl",
  kzqmXN: "xfo62xy",
  "$$css": true
};
const sx = {
  thumb: {
    kVAM5u: "x1uhoqr",
    kaIpWk: "x16rqkct",
    ksu8eU: "x1y0btm7",
    kMzoRj: "xdh2fpr",
    kWkggS: "x83z2og",
    kkrTdU: "x1ypdohk",
    kZKoxP: "x1qx5ct2",
    kzqmXN: "xw4jnvo",
    $$css: true
  },
  tick: (position: number) => [_temp, {
    "--x-left": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`${position}%`)
  }],
  tickActive: {
    kWkggS: "x1fuwk4f",
    $$css: true
  },
  tickLabel: {
    kmkexE: "x1gwx9ft",
    kMwMTN: "x143ll9a",
    kkrTdU: "x1ypdohk",
    kGuDYH: "xif65rj",
    k7Eaqz: "x1pshirs",
    $$css: true
  },
  labelActive: {
    kMwMTN: "xod0wng",
    kkrTdU: "x1sqbtui",
    k63SB2: "x117nqv4",
    $$css: true
  }
};